/**
 * Site-wide footer displaying brand info, navigation links,
 * supported platforms, and contact details.
 */

import React from 'react';
import { Box, Container, Typography, Grid2 as Grid, Link, Divider, alpha } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GREEN, DARK_BLUE } from '../theme';

/** Sticky footer rendered below all page content with grouped navigation columns. */
export default function Footer() {
  const { t } = useTranslation();
  return (
    <Box
      component="footer"
      role="contentinfo"
      sx={{
        bgcolor: 'primary.main',
        color: 'white',
        mt: 'auto',
        pt: 6,
        pb: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent line */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${GREEN}, ${alpha(GREEN, 0.3)}, ${GREEN})`,
        }}
      />
      {/* Subtle radial glow */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(GREEN, 0.06)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              {t('brand.booge')}<Box component="span" sx={{ color: 'secondary.main' }}>{t('brand.it')}</Box>
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, maxWidth: 300, lineHeight: 1.7 }}>
              {t('footer.tagline')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {t('footer.navigation')}
            </Typography>
            {[
              { label: t('nav.home'), to: '/' },
              { label: t('nav.showcase'), to: '/showcase' },
              { label: t('nav.getStarted'), to: '/register' },
            ].map((link) => (
              <Link
                key={link.label}
                component={RouterLink}
                to={link.to}
                color="inherit"
                underline="none"
                display="block"
                sx={{
                  mb: 0.75,
                  opacity: 0.8,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateX(4px)',
                    color: GREEN,
                  },
                }}
              >
                {link.label}
              </Link>
            ))}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {t('footer.platforms')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.8 }}>{t('footer.platformsMobile')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.8 }}>{t('footer.platformsDesktop')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.8 }}>{t('footer.platformsWeb')}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              {t('footer.contact')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.8,
                '&:hover': { opacity: 1, color: GREEN },
                transition: 'all 0.2s ease',
              }}
            >
              info@booge-it.com
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
        <Typography variant="body2" align="center" sx={{ opacity: 0.5 }}>
          {t('footer.copyright')}
        </Typography>
      </Container>
    </Box>
  );
}
